import React, { useEffect, useReducer } from 'react';
import PropTypes, { InferProps } from 'prop-types';
import Papa from 'papaparse';

import Textfield from '../../components/Textfield/Textfield.tsx';
import Dropdown from '../../components/Dropdown/Dropdown.tsx';

import reducer, {
  initialState,
  SET_DATA,
  FORM_SET_FIELD,
  FORM_SUBMIT,
  FORM_CLEAR,
} from './reducer.ts';
import AppLayout from './AppLayout';

const propTypes = {
  url: PropTypes.string.isRequired,
};

export default function App(props: InferProps<typeof propTypes>): JSX.Element {
  const { url } = props;

  const [state, dispatch] = useReducer(reducer, initialState);

  const headerMap = {
    Provider_Type: 'providerType',
    Provider_Number: 'providerNumber',
    Provider_Type_Number: 'providerTypeNumber',
    Name: 'name',
    Phone_Number: 'number',
    Firm_Name: 'firm',
    City: 'city',
    Region: 'region',
    Law_Type: 'lawType',
    Type_Of_Case: 'typeOfCase',
    Subcategory: 'subcategory',
    Search_Results: 'searchResults',
  };

  // Get CSV file
  useEffect(() => {
    Papa.parse(url, {
      header: true,
      transformHeader: (header, index) => headerMap[header] ?? header,
      transform: (value) => value.trim(),
      download: true,
      complete: (result) => dispatch({ type: SET_DATA, payload: result.data }),
    });
  }, []);

  // Make form fields
  const makeFields = () => {
    const {
      firms,
      cities,
      filteredCities,
      regions,
      typesOfCase,
      subcategories,
    } = state.data;
    const { name, firm, city, region, typeOfCase, subcategory } = state.form;

    const handleChange = (e) =>
      dispatch({
        type: FORM_SET_FIELD,
        payload: { name: e.target.name, value: e.target.value },
      });
    return [
      <Textfield
        key="name"
        name="name"
        label="Name"
        value={name}
        onChange={handleChange}
      />,
      <Dropdown
        key="firm"
        name="firm"
        label="Firm"
        emptyValue="Any"
        data={firms}
        value={firm}
        onChange={handleChange}
      />,
      <Dropdown
        key="region"
        name="region"
        label="Region"
        emptyValue="Any"
        data={Object.keys(regions).sort()}
        value={region}
        onChange={handleChange}
      />,
      <Dropdown
        key="city"
        name="city"
        label="Town"
        emptyValue="Any"
        data={filteredCities.length > 0 ? filteredCities : cities}
        value={city}
        onChange={handleChange}
      />,
      <Dropdown
        key="typeOfCase"
        name="typeOfCase"
        label="Law Type"
        emptyValue="Any"
        data={Object.keys(typesOfCase).sort()}
        value={typeOfCase}
        onChange={handleChange}
      />,
      <Dropdown
        key="subcategory"
        name="subcategory"
        label="Subcategory"
        emptyValue="Any"
        data={subcategories}
        value={subcategory}
        onChange={handleChange}
        disabled={!typeOfCase}
      />,
    ];
  };

  return (
    <AppLayout
      loaded={state.data.loaded}
      fields={makeFields()}
      submitted={state.submitted}
      results={state.results}
      handleSubmit={(e) => {
        e.preventDefault();
        dispatch({ type: FORM_SUBMIT });
      }}
      handleClear={(e) => {
        e.preventDefault();
        dispatch({ type: FORM_CLEAR });
      }}
    />
  );
}
App.propTypes = propTypes;
